import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import {ApiMode, ResponseWrapper} from '@shared-library';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import {ConfigService} from '@shared-library';

export const FASTEN_AUTH_VAULT_COOKIE_NAME = 'fasten_connect_auth_vault'
export const FASTEN_AUTH_VAULT_SESSION_CACHE_KEY = 'fasten_connect_auth_vault_session'

export interface AuthSessionInfo {
  active: boolean
  scope?: string
  exp: number
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {


  public IsAuthenticatedSubject = new BehaviorSubject<boolean>(false)

  constructor(private _httpClient: HttpClient, private configService: ConfigService) {}




  public async VaultAuthBegin(email: string, cspPromptForce?: boolean): Promise<any> {
    let resp = await this._httpClient.post<ResponseWrapper>(`${environment.connect_api_endpoint_base}/bridge/vault_auth_begin`, {
      "email": email,
      "csp_prompt_force": cspPromptForce,
    }, { withCredentials: true, params: {"public_id": this.configService.systemConfig$.publicId} }).toPromise()
    return resp
  }

  public async VaultAuthFinish(email: string, code: string): Promise<any> {
    let resp = await this._httpClient.post<ResponseWrapper>(`${environment.connect_api_endpoint_base}/bridge/vault_auth_finish`, {
      "email": email,
      "code": code,
      "csp_prompt_force": this.configService.systemConfig$.apiMode == ApiMode.Live, //always force CSP verification in live mode
    }, { withCredentials: true, params: {"public_id": this.configService.systemConfig$.publicId} }).toPromise()
    return resp
  }

  public async Signout(): Promise<any> {
    this.publishAuthenticationState(false)
    this.clearSessionCache()
    return this.deleteCookie(FASTEN_AUTH_VAULT_COOKIE_NAME)
    // // let remotePouchDb = new PouchDB(this.getRemoteUserDb(localStorage.getItem("current_user")), {skip_setup: true});
    // if(this.pouchDb){
    //   await this.pouchDb.logOut()
    // }
    // await this.Close()
  }


  public async GetSessionInfo(): Promise<AuthSessionInfo | null> {
    const cachedSessionInfo = this.getCachedSessionInfo()
    if (cachedSessionInfo) {
      this.publishAuthenticationState(true)
      return cachedSessionInfo
    }

    try {
      const sessionInfo = await firstValueFrom(this._httpClient.post<AuthSessionInfo>(
        `${environment.identity_api_endpoint_base}/oauth2/introspect`,
        null,
        {withCredentials: true},
      ))

      if (this.isSessionInfoActive(sessionInfo)) {
        this.cacheSessionInfo(sessionInfo)
        this.publishAuthenticationState(true)
        return sessionInfo
      }
    } catch (e) {
      console.error("failed to introspect auth session:", e)
    }

    this.clearSessionCache()
    this.publishAuthenticationState(false)
    return null
  }

  public async IsAuthenticated(): Promise<boolean> {
    let sessionInfo = await this.GetSessionInfo()
    let isAuthenticated = sessionInfo != null
    this.publishAuthenticationState(isAuthenticated)
    return isAuthenticated
  }

  private getCachedSessionInfo(): AuthSessionInfo | null {
    const rawSessionInfo = localStorage.getItem(FASTEN_AUTH_VAULT_SESSION_CACHE_KEY)
    if (!rawSessionInfo) {
      return null
    }

    try {
      const sessionInfo = JSON.parse(rawSessionInfo) as AuthSessionInfo
      if (this.isSessionInfoActive(sessionInfo)) {
        return sessionInfo
      }
    } catch (e) {
      console.error("failed to parse cached auth session:", e)
    }

    this.clearSessionCache()
    return null
  }

  private cacheSessionInfo(sessionInfo: AuthSessionInfo): void {
    localStorage.setItem(FASTEN_AUTH_VAULT_SESSION_CACHE_KEY, JSON.stringify(sessionInfo))
  }

  private clearSessionCache(): void {
    localStorage.removeItem(FASTEN_AUTH_VAULT_SESSION_CACHE_KEY)
  }

  private isSessionInfoActive(sessionInfo?: AuthSessionInfo | null): boolean {
    if (!sessionInfo?.active || !sessionInfo.exp) {
      return false
    }

    return sessionInfo.exp > Math.floor(Date.now() / 1000)
  }

  private deleteCookie(name: string) {
    this.setCookie(name, '', -99999); // - 1 was not far back enough to actually remove the cookie, this sets the time before the epoch and will cause the cookie to be deleted.
  }
  private setCookie(name: string, value: string, expireDays: number, path: string = '') {
    const d: Date = new Date();
    d.setTime(d.getTime() + expireDays * 24 * 60 * 60 * 1000);
    const expires = `expires=${d.toUTCString()}`;
    const cpath = path ? `; path=${path}` : '';
    document.cookie = `${name}=${value}; ${expires}${cpath}; SameSite=Lax`;
  }

  private publishAuthenticationState(authenticated: boolean){
    if(this.IsAuthenticatedSubject.value != authenticated){
      this.IsAuthenticatedSubject.next(authenticated)
    }
  }
}
